<template>
  <div class="settings-page">
    <h2>MCP</h2>
    <div class="subtitle">
      Connect third-party Model Context Protocol servers so their tools become
      available to Routine's agents, gated by the trust level you pick per
      server.
    </div>

    <div class="catalog-hero">
      <div class="catalog-hero-copy">
        <h3>Find servers</h3>
        <p>
          Browse the community catalog of Model Context Protocol servers, then
          add the ones you want below.
        </p>
      </div>
      <BaseButton
        kind="main"
        type="primary"
        text="Browse catalog"
        @click="openUrl(MCP_SERVERS_CATALOG_URL)"
      />
    </div>

    <div class="alert-container" v-if="!in_electron">
      <DownloadButton
        kind="main"
        type="secondary"
        text="Get the desktop app"
        message="External MCP servers are only supported in the Desktop apps."
      />
    </div>

    <template v-else>
      <div class="mcp-servers">
        <div v-if="servers.length === 0 && !editing" class="empty">
          No servers configured yet.
        </div>

        <div v-for="server in servers" :key="server.id" class="mcp-server">
          <div class="row">
            <div class="info">
              <div class="title">
                <h3>{{ server.name }}</h3>
                <div class="status-pill" :class="statusOf(server).kind">
                  <span class="status-dot" />
                  <span>{{ statusOf(server).label }}</span>
                </div>
                <div class="trust-pill" :class="server.trust">
                  {{ server.trust === "all" ? "All tools" : "Read-only tools" }}
                </div>
              </div>
              <p class="url">{{ transportSummary(server) }}</p>
              <p class="tools" v-if="(server.tools ?? []).length > 0">
                {{ (server.tools ?? []).length }} tool{{
                  (server.tools ?? []).length === 1 ? "" : "s"
                }}
                available:
                {{ (server.tools ?? []).map((t) => t.name).join(", ") }}
              </p>
              <p class="error" v-if="statusMessages[server.id]">
                {{ statusMessages[server.id] }}
              </p>
            </div>
            <div class="actions">
              <label class="toggle">
                <input
                  type="checkbox"
                  :checked="server.enabled"
                  :disabled="busyId === server.id"
                  @change="toggle(server)"
                />
                <span>Enabled</span>
              </label>
              <BaseButton
                v-if="oauthAvailable[server.id]"
                kind="main"
                type="primary"
                text="Authorize"
                :state="
                  authorizingId === server.id
                    ? 'loading'
                    : busyId === server.id
                      ? 'disabled'
                      : 'default'
                "
                @click="authorize(server)"
              />
              <BaseButton
                kind="main"
                type="secondary"
                text="Test"
                :state="
                  authorizingId === server.id
                    ? 'disabled'
                    : busyId === server.id
                      ? 'loading'
                      : 'default'
                "
                @click="test(server)"
              />
              <BaseButton
                kind="main"
                type="secondary"
                text="Edit"
                :state="busyId === server.id ? 'disabled' : 'default'"
                @click="edit(server)"
              />
              <BaseButton
                kind="main"
                type="secondary"
                text="Remove"
                tint="red"
                :state="busyId === server.id ? 'disabled' : 'default'"
                @click="remove(server)"
              />
            </div>
          </div>
        </div>
      </div>

      <form v-if="editing" class="mcp-form" @submit.prevent="save">
        <h3>{{ form.id ? "Edit server" : "Add a server" }}</h3>
        <label>
          <span>Name</span>
          <input v-model="form.name" type="text" placeholder="My MCP server" />
        </label>
        <label>
          <span>Transport</span>
          <select v-model="form.transportKind">
            <option value="http">Streamable HTTP (remote)</option>
            <option value="stdio">stdio (local process)</option>
          </select>
        </label>
        <label v-if="form.transportKind === 'http'">
          <span>Server URL</span>
          <input
            v-model="form.url"
            type="url"
            placeholder="https://example.com/mcp"
          />
        </label>
        <template v-if="form.transportKind === 'stdio'">
          <label>
            <span>Command</span>
            <input v-model="form.command" type="text" placeholder="npx" />
          </label>
          <label>
            <span>Arguments (space-separated)</span>
            <input
              v-model="form.argsText"
              type="text"
              placeholder="-y @modelcontextprotocol/server-filesystem /path"
            />
          </label>
          <label>
            <span>Environment variables (one KEY=VALUE per line)</span>
            <textarea
              v-model="form.envText"
              rows="3"
              placeholder="API_KEY=secret"
            />
          </label>
        </template>
        <label v-if="form.transportKind === 'http'">
          <span>Authentication</span>
          <select v-model="form.authKind">
            <option value="none">None</option>
            <option value="bearer">Bearer token</option>
            <option value="header">Custom header</option>
          </select>
        </label>
        <label
          v-if="form.transportKind === 'http' && form.authKind === 'bearer'"
        >
          <span>Token</span>
          <input v-model="form.token" type="password" placeholder="Token" />
        </label>
        <template
          v-if="form.transportKind === 'http' && form.authKind === 'header'"
        >
          <label>
            <span>Header name</span>
            <input
              v-model="form.headerName"
              type="text"
              placeholder="X-Api-Key"
            />
          </label>
          <label>
            <span>Header value</span>
            <input
              v-model="form.headerValue"
              type="password"
              placeholder="Value"
            />
          </label>
        </template>
        <label>
          <span>Trust</span>
          <select v-model="form.trust">
            <option value="readOnly">Read-only tools (recommended)</option>
            <option value="all">All tools (can modify data & act)</option>
          </select>
        </label>
        <p class="hint">
          Read-only exposes only tools the server marks as non-modifying — the
          agent physically cannot invoke anything else. Choose "All tools" only
          for servers you fully trust.
        </p>
        <div class="form-actions">
          <BaseButton
            kind="main"
            type="secondary"
            text="Cancel"
            @click="cancelEdit"
          />
          <BaseButton
            kind="main"
            type="primary"
            :text="form.id ? 'Save & test' : 'Add & test'"
            :state="saving ? 'loading' : canSave ? 'default' : 'disabled'"
            @click="save"
          />
        </div>
      </form>
      <div v-else class="add-row">
        <BaseButton
          kind="main"
          type="primary"
          text="Add server"
          @click="startAdd"
        />
      </div>
    </template>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, reactive, ref } from "vue";

import type {
  ExternalMcpAuth,
  ExternalMcpEnvVar,
  ExternalMcpServer,
  ExternalMcpStatus,
  ExternalMcpTransport,
  ExternalMcpTrust,
} from "@ctrl/controller-rpc";

import BaseButton from "@/components/base/BaseButton.vue";
import DownloadButton from "@/components/base/DownloadButton.vue";
import * as inject from "@/components/inject";
import { in_electron } from "@/utils/in";
import { openExternalUrl } from "@/utils/open-external-url";
import { errorMessage } from "@/utils/unknown";

const MCP_SERVERS_CATALOG_URL = "https://mcpservers.org/";

type AuthKind = "none" | "bearer" | "header";
type TransportKind = "http" | "stdio";

interface FormState {
  id: string | null;
  name: string;
  transportKind: TransportKind;
  url: string;
  command: string;
  argsText: string;
  envText: string;
  authKind: AuthKind;
  token: string;
  headerName: string;
  headerValue: string;
  trust: ExternalMcpTrust;
}

const emptyForm = (): FormState => ({
  id: null,
  name: "",
  transportKind: "http",
  url: "",
  command: "",
  argsText: "",
  envText: "",
  authKind: "none",
  token: "",
  headerName: "",
  headerValue: "",
  trust: "all",
});

export default defineComponent({
  components: { BaseButton, DownloadButton },
  async setup() {
    const controller = inject.controller();
    const integrations = inject.integrations();

    const servers = ref<ExternalMcpServer[]>([]);
    const editing = ref<FormState | null>(null);
    const form = reactive<FormState>(emptyForm());
    const saving = ref(false);
    const busyId = ref<string | null>(null);
    const authorizingId = ref<string | null>(null);
    // Per-server connection message from the last probe (id -> message).
    const statusMessages = reactive<Record<string, string>>({});
    const oauthAvailable = reactive<Record<string, boolean>>({});

    const openUrl = (url: string) => openExternalUrl(url, controller);

    const load = async () => {
      if (!in_electron) return;
      servers.value = await controller.externalMcp.list();
      // Keep the app-wide store in step so composer candidates and
      // advertised pills reflect the mutation immediately.
      void integrations.mcp.refresh();
    };

    if (in_electron) await load();

    const transportSummary = (server: ExternalMcpServer): string => {
      const t = server.transport;
      return "url" in t ? t.url : [t.command, ...(t.args ?? [])].join(" ");
    };

    const statusOf = (server: ExternalMcpServer) => {
      if (!server.enabled)
        return { kind: "pending", label: "Disabled" } as const;
      if (statusMessages[server.id])
        return { kind: "error", label: "Error" } as const;
      if ((server.tools ?? []).length > 0)
        return { kind: "ready", label: "Connected" } as const;
      return { kind: "checking", label: "Unknown" } as const;
    };

    const canSave = computed(() => {
      if (form.name.trim().length === 0) return false;
      return form.transportKind === "http"
        ? form.url.trim().length > 0
        : form.command.trim().length > 0;
    });

    const buildAuth = (): ExternalMcpAuth => {
      // stdio servers authenticate via environment variables, not HTTP auth.
      if (form.transportKind === "stdio") return "none";
      switch (form.authKind) {
        case "bearer":
          return { token: form.token };
        case "header":
          return { name: form.headerName, value: form.headerValue };
        case "none":
          return "none";
      }
    };

    const parseArgs = (text: string): string[] =>
      text.split(/\s+/).filter((s) => s.length > 0);

    const parseEnv = (text: string): ExternalMcpEnvVar[] =>
      text
        .split("\n")
        .map((line) => line.trim())
        .filter((line) => line.length > 0)
        .map((line) => {
          const eq = line.indexOf("=");
          return eq === -1
            ? { name: line, value: "" }
            : { name: line.slice(0, eq).trim(), value: line.slice(eq + 1) };
        });

    const buildTransport = (): ExternalMcpTransport =>
      form.transportKind === "http"
        ? { url: form.url.trim() }
        : {
            command: form.command.trim(),
            args: parseArgs(form.argsText),
            env: parseEnv(form.envText),
          };

    const applyStatus = (status: ExternalMcpStatus | null) => {
      if (!status) return;
      const connection = status.connection;
      oauthAvailable[status.server.id] = connection.kind === "oauthAvailable";
      if (connection.kind === "connected")
        delete statusMessages[status.server.id];
      else statusMessages[status.server.id] = connection.message;
    };

    const startAdd = () => {
      Object.assign(form, emptyForm());
      editing.value = { ...form };
    };

    const edit = (server: ExternalMcpServer) => {
      const auth = server.auth;
      const authKind: AuthKind =
        auth === "none" ? "none" : "token" in auth ? "bearer" : "header";
      const t = server.transport;
      const isHttp = "url" in t;
      Object.assign(form, {
        id: server.id,
        name: server.name,
        transportKind: isHttp ? "http" : "stdio",
        url: "url" in t ? t.url : "",
        command: "command" in t ? t.command : "",
        argsText: "command" in t ? (t.args ?? []).join(" ") : "",
        envText:
          "command" in t
            ? (t.env ?? []).map((e) => `${e.name}=${e.value}`).join("\n")
            : "",
        authKind,
        token: auth !== "none" && "token" in auth ? auth.token : "",
        headerName: auth !== "none" && "name" in auth ? auth.name : "",
        headerValue: auth !== "none" && "value" in auth ? auth.value : "",
        trust: server.trust,
      });
      editing.value = { ...form };
    };

    const cancelEdit = () => {
      editing.value = null;
    };

    const save = async () => {
      if (!canSave.value || saving.value) return;
      saving.value = true;
      try {
        const transport = buildTransport();
        const auth = buildAuth();
        const status = form.id
          ? await controller.externalMcp.update(
              form.id,
              form.name.trim(),
              transport,
              auth,
              form.trust
            )
          : await controller.externalMcp.add(
              form.name.trim(),
              transport,
              auth,
              form.trust
            );
        applyStatus(status);
        editing.value = null;
        await load();
      } catch (error) {
        controller.notifications.notify(errorMessage(error));
      } finally {
        saving.value = false;
      }
    };

    const toggle = async (server: ExternalMcpServer) => {
      busyId.value = server.id;
      try {
        await controller.externalMcp.setEnabled(server.id, !server.enabled);
        await load();
      } catch (error) {
        controller.notifications.notify(errorMessage(error));
      } finally {
        busyId.value = null;
      }
    };

    const test = async (server: ExternalMcpServer) => {
      busyId.value = server.id;
      try {
        const status = await controller.externalMcp.test(server.id);
        applyStatus(status);
        await load();
      } catch (error) {
        controller.notifications.notify(errorMessage(error));
      } finally {
        busyId.value = null;
      }
    };

    const authorize = async (server: ExternalMcpServer) => {
      if (busyId.value) return;
      busyId.value = server.id;
      authorizingId.value = server.id;
      try {
        const error = await controller.externalMcp.authorize(server.id);
        if (error) {
          statusMessages[server.id] = error;
          return;
        }
        const status = await controller.externalMcp.test(server.id);
        applyStatus(status);
        await load();
      } catch (error) {
        controller.notifications.notify(errorMessage(error));
      } finally {
        authorizingId.value = null;
        busyId.value = null;
      }
    };

    const remove = async (server: ExternalMcpServer) => {
      busyId.value = server.id;
      try {
        await controller.externalMcp.remove(server.id);
        delete statusMessages[server.id];
        delete oauthAvailable[server.id];
        if (editing.value?.id === server.id) editing.value = null;
        await load();
      } catch (error) {
        controller.notifications.notify(errorMessage(error));
      } finally {
        busyId.value = null;
      }
    };

    return {
      MCP_SERVERS_CATALOG_URL,
      in_electron,
      servers,
      editing,
      form,
      saving,
      busyId,
      authorizingId,
      statusMessages,
      oauthAvailable,
      canSave,
      openUrl,
      transportSummary,
      statusOf,
      startAdd,
      edit,
      cancelEdit,
      save,
      toggle,
      test,
      authorize,
      remove,
    };
  },
});
</script>

<style scoped lang="scss">
@use "../../styles/settings.scss";

.catalog-hero {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  width: 100%;
  margin: 24px 0 8px;
  padding: 32px 24px;
  border-radius: $border-radius-l;
  row-gap: 12px;
  @include themed() {
    background-color: t($grey-05);
    border: 1px solid t($grey-10);
  }

  .catalog-hero-copy {
    display: flex;
    flex-direction: column;
    align-items: center;
    row-gap: 8px;
    max-width: 420px;

    h3 {
      margin: 0;
      @extend %font-h4;
      @include themed() {
        color: t($primary);
      }
    }

    p {
      margin: 0;
      @extend %font-caption-regular;
      @include themed() {
        color: t($grey-60);
      }
    }
  }

  .catalog-hero-link {
    @extend %font-caption-regular;
    text-decoration: none;
    @include themed() {
      color: t($grey-60);
    }

    &:hover {
      text-decoration: underline;
      @include themed() {
        color: t($primary);
      }
    }
  }
}

.mcp-servers {
  display: flex;
  flex-direction: column;
  width: 100%;

  .empty {
    @extend %font-caption-regular;
    padding: 16px 0;
    @include themed() {
      color: t($grey-60);
    }
  }

  .mcp-server {
    padding: 20px 0;
    @include themed() {
      border-bottom: 1px solid t($grey-10);
    }

    .row {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      column-gap: 24px;
    }

    .title {
      display: flex;
      align-items: center;
      column-gap: 8px;
      margin-bottom: 4px;

      h3 {
        margin: 0;
        @extend %font-body-medium;
      }
    }

    p {
      margin: 2px 0;
      @extend %font-caption-regular;
      @include themed() {
        color: t($grey-60);
      }

      &.error {
        color: $red;
        overflow-wrap: anywhere;
      }
    }

    .actions {
      display: flex;
      align-items: center;
      column-gap: 8px;
      flex-shrink: 0;

      .toggle {
        display: flex;
        align-items: center;
        column-gap: 6px;
        @extend %font-caption-regular;
      }
    }
  }
}

.trust-pill {
  border-radius: 999px;
  padding: 2px 8px;
  @extend %font-caption-medium;
  @include themed() {
    background-color: t($grey-05);
    color: t($grey-60);
  }

  &.all {
    color: $starred;
  }
}

.status-pill {
  display: inline-flex;
  align-items: center;
  column-gap: 6px;
  border-radius: 999px;
  padding: 3px 8px;
  @extend %font-caption-medium;
  @include themed() {
    background-color: t($grey-05);
    color: t($grey-60);
  }

  .status-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background-color: currentColor;
  }

  &.ready {
    color: $green;
  }
  &.error {
    color: $red;
  }
  &.pending,
  &.checking {
    color: $starred;
  }
}

.mcp-form {
  display: flex;
  flex-direction: column;
  row-gap: 12px;
  max-width: 480px;
  padding: 20px 0;

  h3 {
    margin: 0;
    @extend %font-body-medium;
  }

  label {
    display: flex;
    flex-direction: column;
    row-gap: 4px;
    @extend %font-caption-medium;

    input,
    select,
    textarea {
      padding: 8px 10px;
      border-radius: $border-radius-m;
      font-family: inherit;
      resize: vertical;
      @include themed() {
        border: 1px solid t($grey-10);
        background-color: t($grey-03);
        color: t($grey-90);
      }
    }
  }

  .hint {
    margin: 0;
    @extend %font-caption-regular;
    @include themed() {
      color: t($grey-60);
    }
  }

  .form-actions {
    display: flex;
    column-gap: 8px;
    margin-top: 4px;
  }
}

.add-row {
  padding: 16px 0;
}

.alert-container {
  width: 100%;
  padding: 16px 0;
}
</style>
